import { Box, Button, IconButton, Modal, Slide } from '@mui/material'
import { ArrowLeft } from '../../../../../core/global/Icons'
import { useContext, useEffect, useState } from 'react'
import { ToolContext } from '../../../../../core/context/ToolContext'
import { rqx } from '../../../../../core/request/API'
import { env } from '../../../../../core/Env'
import { uploadToS3Presigned } from '../../../../../core/transmit/S3'
import { UseStyles } from '../../../../mycloud/global/DesktopDsg'
import { SnakeLoader } from '../../../../../core/loader/SnakeLoader'
import { useDropzone } from 'react-dropzone'
import { Details } from '../setup/Details'
import { Review } from '../setup/Review'
import moment from 'moment'

const emptyForm = guideline => ({
    departmentname: {
        id: guideline?.company_department_id || guideline?.departmentId || 0,
        name: guideline?.dept_name || ''
    },
    policyname: guideline?.policyname || guideline?.policy_name || '',
    description: '',
    dateupload: new Date(),
    dateissued: '',
    preparedby: '',
    preparedname: '',
    approvedby: '',
    approvedname: '',
    file_name: '',
    file_size: '',
    file_type: '',
    file_link: '',
    file: ''
})

export const CreateVersion = ({ open, guideline, onClose, onCreated }) => {
    const { tool_state } = useContext(ToolContext)
    const access = tool_state.__SESSION.data.uaccess[0]
    const userId = parseInt(atob(tool_state.__SESSION.data.ainfo.aid).split('_', 2)[1])
    const dsg = UseStyles()
    const [form, setForm] = useState(() => emptyForm(guideline))
    const [users, setUsers] = useState([])
    const [step, setStep] = useState('details')
    const [creating, setCreating] = useState(false)
    const [error, setError] = useState('')

    useEffect(() => {
        if (!open) return
        setForm(emptyForm(guideline))
        setStep('details')
        setCreating(false)
        setError('')
        const loadUsers = async() => {
            const response = await rqx.get(`<<global>>/${env()}/tooluser/gettooluser`, {
                company_id: access.company_id || 0,
                tool_id: process.env.REACT_APP_TID,
                ref: 'active'
            })
            setUsers([...(response.superadmin || []), ...(response.users || [])].map(user => ({
                ...user,
                uname: `${user.first_name} ${user.last_name}`
            })))
        }
        loadUsers()
    }, [access.company_id, guideline, open])

    const onDrop = files => {
        if (!files.length) {
            setError('Only PDF file formats are allowed for upload.')
            return
        }
        if (files[0].size > 4194304) {
            setError('File upload must not exceed 3MB.')
            return
        }
        const file = files[0]
        setError('')
        setForm(current => ({
            ...current,
            file: files,
            file_name: file.name,
            file_size: file.size,
            file_type: file.type,
            file_link: URL.createObjectURL(file)
        }))
    }
    const { getRootProps, getInputProps } = useDropzone({
        onDrop,
        accept: { 'application/pdf': ['.pdf'] },
        multiple: false
    })

    if (!guideline) return null
    const guidelineNumber = guideline.policy_num || guideline.series_num || ''
    const currentVersion = Math.max(...[guideline, ...(guideline.versions || [])]
        .map(version => version.version_number || 1))
    const newVersion = currentVersion + 1
    const valid = form.file_name && form.description && form.dateissued && form.preparedby && form.approvedby
    const match = { params: { params: step === 'review' ? 'review' : 'create' } }
    const plcysn = { data: guidelineNumber }
    const pName = { data: false, set: () => {} }
    const close = () => {
        if (creating) return
        setForm(emptyForm(guideline))
        setStep('details')
        onClose()
    }
    const save = async() => {
        if (!valid || creating) return
        setCreating(true)
        setError('')
        try {
            const uploaded = await uploadToS3Presigned(form.file, {
                requestRef: 'presigned-upload',
                feature_ref: 'policies',
                tool_id: process.env.REACT_APP_TOOL_ID,
                privacy: 'public'
            })
            const response = await rqx.post(
                `<<tool>>/${env() === 'dev' ? 'local' : env()}/general-maintenance/cloud/policy/create`,
                {
                    parent_id: guideline.parent_id || guideline.id,
                    company_id: access.company_id,
                    description: form.description,
                    dateissued: moment(form.dateissued).format('YYYY-MM-DD'),
                    preparedby: form.preparedby,
                    approvedby: form.approvedby,
                    file_name: form.file_name,
                    file_type: form.file_type,
                    file_size: form.file_size,
                    file_link: uploaded.rqxPreSigned.location,
                    user_id: userId
                }
            )
            if (response.message === 'success') {
                setForm(emptyForm(guideline))
                await onCreated()
            } else {
                setError(response.message || 'Unable to create the guideline version.')
            }
        } catch (requestError) {
            setError(requestError.message || 'Unable to create the guideline version.')
        } finally {
            setCreating(false)
        }
    }

    return <Modal open={open} sx={{ zIndex: 1304 }}>
        <Slide in={open} direction="left" className="noutlined">
            <Box height="100%" width="100%" bgcolor="#F0F5F9" display="flex" flexDirection="column">
                <Box minHeight="86px" p="12px 16px" width="100%">
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                        <IconButton disabled={creating} sx={{ p: 0 }} onClick={close}>
                            <ArrowLeft stroke="#0000008a" width={16} height={16} />
                        </IconButton>
                        <Box display="flex" justifyContent="center" fontSize="16px" fontWeight={600} lineHeight="26px">
                            Create New Guideline Version
                        </Box>
                        <Box width={16} />
                    </Box>
                    <Box py="12px" display="flex" justifyContent="space-between" gap={1}
                        fontSize="12px" lineHeight="19px">
                        <Box width="100%" textAlign="center" color={step === 'details' ? '#107038' : '#737373'}>
                            <Box>Details</Box>
                            <Box height="4px" bgcolor={step === 'details' ? '#107038' : '#737373'} borderRadius="12px" />
                        </Box>
                        <Box width="100%" textAlign="center" color={step === 'review' ? '#107038' : '#737373'}>
                            <Box>Review</Box>
                            <Box height="4px" bgcolor={step === 'review' ? '#107038' : '#737373'} borderRadius="12px" />
                        </Box>
                    </Box>
                </Box>
                <Box height="calc(100% - 162px)" minHeight={0} bgcolor="#FFFFFF"
                    borderRadius="12px 12px 0 0" p="0px 16px 16px" className="overflowY"
                    sx={{ overflowY: 'auto', WebkitOverflowScrolling: 'touch', touchAction: 'pan-y' }}>
                    <Box display="flex" justifyContent="center" my="11px">
                        <Box height="2px" width="37px" bgcolor="#EAEFF7" />
                    </Box>
                    {step === 'review' ?
                        <Review form={form} plcysn={plcysn} guidelineNumber={guidelineNumber}
                            currentVersion={currentVersion} newVersion={newVersion} />
                    :
                        <Details dsg={dsg} form={form} setForm={setForm} match={match}
                            setOpen={() => {}} __SESSION={tool_state.__SESSION} plcysn={plcysn}
                            handlerBtn={() => {}} pName={pName} allUsers={users}
                            getRootProps={getRootProps} getInputProps={getInputProps}
                            versionMode guidelineNumber={guidelineNumber}
                            currentVersion={currentVersion} newVersion={newVersion} />}
                    {error && <Box color="#D32F2F" fontSize="12px" mt={2}>{error}</Box>}
                </Box>
                <Box minHeight="76px" p="16px" bgcolor="#FEFEFE"
                    boxShadow="0px 0px 24px rgba(0, 0, 0, 0.08)">
                    {step === 'review' ?
                        <Box display="flex" alignItems="center" gap="9px">
                            <Button variant="contained" fullWidth disabled={creating} onClick={() => setStep('details')}
                                sx={{ bgcolor: '#E2EDE7', border: '1px solid #107038', color: '#107038' }}>
                                Edit
                            </Button>
                            <Button variant="contained" fullWidth disabled={creating} onClick={save}>
                                {creating ? <Box display="flex" alignItems="center">
                                    <Box mr="10px">Saving</Box>
                                    <SnakeLoader size="5px" bg="#FFFFFF" distance="0.1rem" />
                                </Box> : 'Save'}
                            </Button>
                        </Box>
                    :
                        <Button variant="contained" fullWidth disabled={!valid} onClick={() => setStep('review')}
                            sx={{ backgroundColor: !valid && '#C7DAD7' }}>
                            Next
                        </Button>}
                </Box>
            </Box>
        </Slide>
    </Modal>
}
